import * as Mutations from './mutations';
import { SchoolService } from '@/common/services/settings/school.service';
import { ActivitiesService } from '@/common/services/pages/activities.service';
import { DEFAULT_STATE } from './default-state';
import * as _ from 'lodash';
import { SchoolClassService } from '@/common/services/pages/classes.service';
import * as moment from 'moment';
import { MOMENT_BACKEND_DATE_FORMAT } from '@/constants/dateFormats';
import { SCHOOL_APP } from '@/main';
import { mapWithClientID } from '@/common/utils/objects';
import  priceFormat  from '@/filters/priceFormat';

export const DashboardModule = {
    state: _.cloneDeep(DEFAULT_STATE),
    mutations: {
        [Mutations.SET_LAST_SIGN_UPS] (state, lastSignUps) {
            state.lastSignUps = lastSignUps;
        },

        [Mutations.SET_DASHBOARD_CALENDAR_DATE] (state, date) {
            state.date = date;
        },

        [Mutations.SET_DASHBOARD_CALENDAR_ACTIVITY_TYPE] (state, type) {
            state.activityType = type;
        },

        [Mutations.SET_DASHBOARD_ACTIVITIES] (state, activities) {
            state.activities = activities.map(mapWithClientID);
        },

        [Mutations.SET_DASHBOARD_RANGE_START_DATE] (state, startDate) {
            state.rangeStartDate = startDate;
        },

        [Mutations.SET_DASHBOARD_RANGE_END_DATE] (state, endDate) {
            state.rangeEndDate = endDate;
        },

        [Mutations.TOGGLE_DASHBOARD_RANGE] (state) {
            state.isOpen = !state.isOpen;
            if (!state.isOpen && state.rangeStartDate && !state.rangeEndDate) {
                state.rangeEndDate = state.rangeStartDate;
            }
        },

        [Mutations.SET_DASHBOARD_RANGE] (state, date) {
            if (state.rangeStartDate) {
                if (state.rangeEndDate) {
                    state.rangeStartDate = date;
                    state.rangeEndDate = null;
                }
                else {
                    state.rangeEndDate = date;
                    state.isOpen = false;
                }
            }
            else if (state.rangeEndDate) {
                state.rangeStartDate = date;
                state.isOpen = false;
            }
            else {
                state.rangeStartDate = date;
            }
        },

        [Mutations.SET_ENROLLMENT] (state, { enrollmentsForClasses, enrollmentsForCamps, enrollmentsForAftercare }) {
            state.enrollmentsForClasses = enrollmentsForClasses;
            state.enrollmentsForCamps = enrollmentsForCamps;
            state.enrollmentsForAftercare = enrollmentsForAftercare;
        },

        [Mutations.SET_DASHBOARD_STATS] (state, { totalRefund, totalRevenue, revenuePerDates } ) {
            state.totalRefund = totalRefund;
            state.totalRevenue = totalRevenue;
            state.revenuePerDates = [['Years', 'Revenue']];
            for (let key in revenuePerDates) {
                state.revenuePerDates.push([moment(key).format('MM-DD-YYYY'), revenuePerDates[key]]);
            }
        },

        [Mutations.RESET_DASHBOARD] (state) {
            Object.assign(state, _.cloneDeep(DEFAULT_STATE));
        }
    },
    actions: {
        async getLastSignUps(context) {
            const { data } = await SchoolService.getLastChildrenSignUps(context.getters.schoolId);
            context.commit(Mutations.SET_LAST_SIGN_UPS, data);
        },

        async getSummaryTransactions(context) {
            const startDate = context.getters.dashboardRangeStartDate;
            const endDate = context.getters.dashboardRangeEndDate;
            if (startDate && endDate) {
                const { data } = await SchoolService.getSummaryTransactions(context.getters.schoolId,
                    moment(startDate).format('YYYY-MM-DDTHH:mm:ss'),
                    moment(endDate).format('YYYY-MM-DDTHH:mm:ss'));
                context.commit(Mutations.SET_DASHBOARD_STATS, data);
            }
        },

        async getEnrolledChildrenForActivities(context) {
            const { data } = await SchoolService.getEnrolledChildrenForActivities(context.getters.schoolId);
            context.commit(Mutations.SET_ENROLLMENT, data);
        },

        async selectDashboardCalendarDate(context, date) {
            context.commit(Mutations.SET_DASHBOARD_CALENDAR_DATE, date);
            await context.dispatch('getActivities');
        },

        async selectDashboardActivityType(context, type) {
            context.commit(Mutations.SET_DASHBOARD_CALENDAR_ACTIVITY_TYPE, type);
            await context.dispatch('getActivities');
        },

        async getActivities(context) {
            const date = context.getters.dashboardCalendarDate;
            const type = context.getters.dashboardCalendarActivityType;
            if (date && type) {
                const getActivities = ActivitiesService[`get${type.split(' ').join('')}ByDate`];
                const { data } = await getActivities(
                    context.getters.schoolId,
                    moment(date).format('YYYY-MM-DD'),
                    context.getters.timezone
                );
                context.commit(Mutations.SET_DASHBOARD_ACTIVITIES, data);
            }
        },

        setTransactionsRangeStartDate(context, rangeStartDate) {
            if (context.getters.dashboardRangeStartDate !== rangeStartDate) {
                context.commit(Mutations.SET_DASHBOARD_RANGE_START_DATE, rangeStartDate);
                context.dispatch('getSummaryTransactions');
            }
        },

        setTransactionsRangeEndDate(context, rangeEndDate) {
            if (context.getters.dashboardRangeEndDate !== rangeEndDate) {
                context.commit(Mutations.SET_DASHBOARD_RANGE_END_DATE, rangeEndDate);
                context.dispatch('getSummaryTransactions');
            }
        },

        selectDashboardRange(context, date) {
            context.commit(Mutations.SET_DASHBOARD_RANGE, date);
            context.dispatch('getSummaryTransactions');
        },

        getTransactionsForLast7Days(context) {
            context.dispatch('setTransactionsRangeStartDate', moment().subtract(6, 'days').toDate());
            context.dispatch('setTransactionsRangeEndDate', new Date());
            SCHOOL_APP.$root.$emit('open-date', moment().toDate());
        },

        getTransactionsForLast30Days(context) {
            context.dispatch('setTransactionsRangeStartDate', moment().subtract(29, 'days').toDate());
            context.dispatch('setTransactionsRangeEndDate', new Date());
            SCHOOL_APP.$root.$emit('open-date', moment().toDate());
        },

        getTransactionsForLastMonth(context) {
            context.dispatch('setTransactionsRangeStartDate', moment().date(1).subtract(1, 'month').toDate());
            context.dispatch('setTransactionsRangeEndDate', moment().date(1).subtract(1, 'days').toDate());
            SCHOOL_APP.$root.$emit('open-date', moment().subtract(1, 'month').toDate());
        },

        setTransactionsForCustomRange(context) {
            context.dispatch('setTransactionsRangeStartDate', moment().toDate());
            context.dispatch('setTransactionsRangeEndDate', moment().toDate());
        }
    },
    getters: {
        lastSignUps: state => state.lastSignUps,

        dashboardCalendarDate: state => state.date,

        dashboardCalendarActivityType: state => state.activityType,

        dashboardActivities: state => state.activities,

        dashboardRangeStartDate: state => state.rangeStartDate,

        dashboardRangeEndDate: state => state.rangeEndDate,

        dashboardRangeOpen: state => state.isOpen,

        dashboardEnrollmentForClasses: state => state.enrollmentsForClasses,

        dashboardEnrollmentForCamps: state => state.enrollmentsForCamps,

        dashboardEnrollmentForAftercare: state => state.enrollmentsForAftercare,

        dashboardTotalRefund: (state, getters, rootState, rootGetters) => state.totalRefund && rootGetters.currencySign + ' ' + priceFormat(state.totalRefund),

        dashboardTotalRevenue: (state, getters, rootState, rootGetters) => state.totalRevenue && rootGetters.currencySign + ' ' + priceFormat(state.totalRevenue),

        dashboardRevenuePerDates: state => state.revenuePerDates
    }
};


// WEBPACK FOOTER //
// ./src/store/modules/dashboard/dashboard.module.js